import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Textarea } from '@/components/ui/textarea'
import { useCan } from '@/hooks/useCan'
import { formatDateTime } from '@/lib/dateUtils'
import { cn } from '@/lib/utils'
import { knowledgeApi, knowledgeKeys, refusal, type KnowledgeReport } from '../api'
import { KnowledgeEntryDialog } from './KnowledgeEntryDialog'

const STATUSES = ['open', 'corrected', 'dismissed', 'all'] as const
type StatusFilter = typeof STATUSES[number]

const TINT: Record<KnowledgeReport['Status'], string> = {
  open: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  corrected: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  dismissed: 'bg-muted text-muted-foreground',
}

function DismissDialog({ report, onClose }: { report: KnowledgeReport; onClose: () => void }) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const dismiss = useMutation({
    mutationFn: () => knowledgeApi.dismiss(report.ReportID, note.trim()),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: knowledgeKeys.all }); onClose() },
    onError: (e) => setError(refusal(e) ?? t('knowledgeBase.saveFailed', 'It could not be saved. Try again.')),
  })
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('knowledgeBase.dismissTitle', 'Dismiss this report')}</DialogTitle>
          <DialogDescription>
            {report.PausedEntries > 0
              ? t('knowledgeBase.dismissPaused', 'Nothing needs to change. The knowledge this report stopped using is put back in use.')
              : t('knowledgeBase.dismissIntro', 'Nothing needs to change.')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="kb-dismiss">{t('knowledgeBase.dismissWhy', 'Why?')}</Label>
          <Textarea id="kb-dismiss" rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={() => dismiss.mutate()} disabled={!note.trim() || dismiss.isPending}>{t('knowledgeBase.dismiss', 'Dismiss')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * "Not right" reports: answers someone said were wrong, in their own words. A report changes nothing
 * by itself; it is closed by adding the correct knowledge, or dismissed with a reason. The question
 * and answer were copied in when it was reported - the reviewer cannot open someone else's
 * conversation.
 */
export function ReportsTab() {
  const { t } = useTranslation()
  const canManage = useCan('knowledge.manage')
  const [status, setStatus] = useState<StatusFilter>('open')
  const [correcting, setCorrecting] = useState<KnowledgeReport | null>(null)
  const [dismissing, setDismissing] = useState<KnowledgeReport | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)
  const list = useQuery({ queryKey: knowledgeKeys.reports(status), queryFn: () => knowledgeApi.reports(status) })
  const options = useQuery({ queryKey: knowledgeKeys.options, queryFn: knowledgeApi.options, enabled: canManage })
  const reports = list.data ?? []

  return (
    <div className="space-y-3">
      <SegmentedControl
        size="sm"
        ariaLabel={t('knowledgeBase.status', 'Status')}
        value={status}
        onValueChange={setStatus}
        options={STATUSES.map((s) => ({ value: s, label: t(`knowledgeBase.report_${s}`, { open: 'Open', corrected: 'Corrected', dismissed: 'Dismissed', all: 'All' }[s]) }))}
      />
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[960px] text-sm">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.colWhen', 'When')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.colWho', 'Who')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.agent', 'AI agent')}</th>
              <th className="w-[24%] px-3 py-2 font-medium">{t('knowledgeBase.colQuestion', 'Question')}</th>
              <th className="w-[30%] px-3 py-2 font-medium">{t('knowledgeBase.colWrong', 'What was wrong')}</th>
              <th className="px-3 py-2 font-medium">{t('knowledgeBase.status', 'Status')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {!list.isLoading && reports.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-muted-foreground">{t('knowledgeBase.noReports', 'No reports.')}</td></tr>
            )}
            {reports.map((r) => (
              <tr key={r.ReportID} className={cn('border-t align-top', r.Status === 'open' && 'bg-amber-50/50 dark:bg-amber-950/20')}>
                <td className="whitespace-nowrap px-3 py-2.5">{formatDateTime(r.ReportedAt)}</td>
                <td className="whitespace-nowrap px-3 py-2.5">{r.ReportedByName ?? '—'}</td>
                <td className="whitespace-nowrap px-3 py-2.5">{r.AgentName ?? '—'}</td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  <div>{r.Question ? `"${r.Question}"` : '—'}</div>
                  {r.Answer && (
                    <button type="button" className="mt-1 text-xs text-primary underline-offset-2 hover:underline"
                            onClick={() => setExpanded(expanded === r.ReportID ? null : r.ReportID)}>
                      {expanded === r.ReportID ? t('knowledgeBase.hideAnswer', 'Hide the answer') : t('knowledgeBase.showAnswer', 'Show the answer')}
                    </button>
                  )}
                  {expanded === r.ReportID && <div className="mt-1 whitespace-pre-wrap rounded bg-muted/50 p-2 text-xs text-foreground">{r.Answer}</div>}
                </td>
                <td className="px-3 py-2.5">
                  <div>{r.WhatWrong}</div>
                  {r.WhatRight && <div className="mt-1 text-xs text-emerald-800 dark:text-emerald-300"><strong>{t('knowledgeBase.suggestedRight', 'Suggested right:')}</strong> {r.WhatRight}</div>}
                  {r.PausedEntries > 0 && r.Status === 'open' && (
                    <div className="mt-1 text-xs text-amber-800 dark:text-amber-300">{t('knowledgeBase.reportPaused', 'The knowledge the answer used is paused until this is reviewed.')}</div>
                  )}
                  {r.Status !== 'open' && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {r.Status === 'corrected'
                        ? t('knowledgeBase.correctedBy', 'Corrected by {{name}}, {{when}}', { name: r.ClosedByName ?? '', when: formatDateTime(r.ClosedAt) })
                        : t('knowledgeBase.dismissedBy', 'Dismissed by {{name}}, {{when}}: {{note}}', { name: r.ClosedByName ?? '', when: formatDateTime(r.ClosedAt), note: r.OutcomeNote ?? '' })}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-[11px]', TINT[r.Status])}>
                    {t(`knowledgeBase.report_${r.Status}`, { open: 'Open', corrected: 'Corrected', dismissed: 'Dismissed' }[r.Status])}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  {canManage && r.Status === 'open' && (
                    <div className="flex flex-col items-start gap-1.5">
                      <Button size="sm" className="h-7 px-2 text-xs" disabled={!options.data} onClick={() => setCorrecting(r)}>
                        {t('knowledgeBase.addCorrect', 'Add the correct knowledge')}
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setDismissing(r)}>
                        {t('knowledgeBase.dismiss', 'Dismiss')}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('knowledgeBase.reportsFooter', 'A report never changes what the AI Assistant says by itself. Adding the correct knowledge closes it.')}
      </p>
      {correcting && options.data && (
        <KnowledgeEntryDialog open onOpenChange={(o) => { if (!o) setCorrecting(null) }} options={options.data} report={correcting} />
      )}
      {dismissing && <DismissDialog report={dismissing} onClose={() => setDismissing(null)} />}
    </div>
  )
}
